import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent
} from '@angular/common/http';
import { Observable } from 'rxjs';
import * as CryptoJS from 'crypto-js';
import { environment } from 'src/environments/environment';

@Injectable()
export class HmacInterceptor implements HttpInterceptor {
  private readonly secretKey = environment.hmac.hmackey;
  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {

    // Unix timestamp (seconds)
    const timestamp = Math.floor(Date.now() / 1000).toString();
    const method = req.method.toUpperCase();

    // ---------------------------------
    // Canonical Path (FIX 1: Use urlWithParams)
    // ---------------------------------
    // req.urlWithParams ensures that HttpParams are included in the signature
    const url = new URL(req.urlWithParams, window.location.origin);

    let path = url.pathname.toLowerCase();
    const apiIndex = path.indexOf('/api');
    if (apiIndex !== -1) {
      path = path.substring(apiIndex);
    }

    // ---------------------------------
    // Canonical Query
    // ---------------------------------
    const queryParams: string[] = [];
    url.searchParams.forEach((value, key) => {
      queryParams.push(
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
      );
    });
    queryParams.sort();
    const canonicalQuery = queryParams.join('&');

    // ---------------------------------
    // Canonical Body (FIX 2: Sync body state)
    // ---------------------------------
    let bodyString = '';
    let processedBody = req.body;

    if (method !== 'GET' && method !== 'DELETE' && req.body != null) {

      // Safety check: if the component already called JSON.stringify(body), parse it 
      // first to prevent double-stringifying (e.g. "\"{\\\"password\\\":...}\"")
      let parsedBody = req.body;
      if (typeof req.body === 'string') {
        try {
          parsedBody = JSON.parse(req.body);
        } catch (e) {
          // Fallback if it's a raw text body that isn't JSON
        }
      }

      processedBody = this.sortObject(parsedBody);
      bodyString = JSON.stringify(processedBody);
    }

    // ---------------------------------
    // Payload (MUST MATCH BACKEND)
    // ---------------------------------
    const payload = [
      method,
      path,
      canonicalQuery,
      bodyString.trim()
    ].join('\n');

    const data = `${timestamp}:${payload}`;
    const key = CryptoJS.enc.Base64.parse(this.secretKey);
    const signature = CryptoJS.enc.Base64.stringify(
      CryptoJS.HmacSHA256(data, key)
    );

    // ---------------------------------
    // Clone & Send (FIX 3: Overwrite outgoing body)
    // ---------------------------------
    const signedReq = req.clone({
      // We must send the exact sorted body that we used to generate the HMAC
      body: method !== 'GET' && method !== 'DELETE' && req.body != null ? processedBody : req.body,
      setHeaders: {
        'X-Timestamp': timestamp,
        'X-Signature': signature
      }
    });

    return next.handle(signedReq);
  }

  /**
   * Recursively sort object keys
   */
  private sortObject(value: any): any {
    if (value instanceof Date) {
      return value;
    }

    //Company details update handled object
     if (value instanceof Date || value instanceof FormData) {
      return value; 
    }

    if (Array.isArray(value)) {
      return value.map(v => this.sortObject(v));
    }

    if (value !== null && typeof value === 'object') {
      return Object.keys(value)
        .sort()
        .reduce((result: any, key) => {
          result[key] = this.sortObject(value[key]);
          return result;
        }, {});
    }

    return value;
  }
}